import { FC, useState } from 'react';
import moment from 'moment';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Box, Paper, Grid, Button, TextField, FormControlLabel, Checkbox, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, RadioGroup, Radio } from '@mui/material';

import { useWorkplacesContext } from 'module/workplaces/context/WorkplacesContext';
import { useToastContext } from 'core/components/Toast/context/ToastContext';
import { copyShifts, fetchAllShifts } from 'core/API/shifts';
import { useQueryClient } from 'react-query';

import './CopySchedulePage.scss';

const validationSchema = Yup.object({
	copyRangeType: Yup.string().required('Please select copy range type'),
	sourceDate: Yup.string().required('Source date is required'),
	targetDate: Yup.string().required('Target date is required'),
	copyExactSchedule: Yup.boolean(),
	copyToHouseSchedule: Yup.boolean(),
});

interface ICopyScheduleForm {
	copyRangeType: 'day' | 'week';
	sourceDate: string;
	targetDate: string;
	copyExactSchedule: boolean;
	copyToHouseSchedule: boolean;
}

interface ICopySchedulePage {
	onBack: () => void;
	currentWeekStart?: string;
	currentWeekEnd?: string;
	startOfWeek?: string; // Day name like "Monday", "Wednesday", etc.
}

const CopySchedulePage: FC<ICopySchedulePage> = ({ onBack, currentWeekStart, startOfWeek = 'Monday' }) => {
	const { showToast } = useToastContext();
	const { workplaceId } = useWorkplacesContext();
	const queryClient = useQueryClient();
	const [isLoading, setIsLoading] = useState(false);
	const [showConfirmDialog, setShowConfirmDialog] = useState(false);
	const [existingShiftCount, setExistingShiftCount] = useState(0);
	const [pendingCopyData, setPendingCopyData] = useState<{
		data: ICopyScheduleForm;
		sourceStartDate: string;
		sourceEndDate: string;
		targetStartDate: string;
	} | null>(null);

	// Helper to get first day of business week based on organization settings
	const getFirstDayOfWeek = (date: string | moment.Moment) => {
		const dateMoment = moment(date);
		const dayMap: { [key: string]: number } = {
			Sunday: 0,
			Monday: 1,
			Tuesday: 2,
			Wednesday: 3,
			Thursday: 4,
			Friday: 5,
			Saturday: 6,
		};

		const startDayOfWeek = dayMap[startOfWeek] ?? 1; // Default to Monday if not found
		const currentDayOfWeek = dateMoment.day();

		let daysBack = currentDayOfWeek - startDayOfWeek;
		if (daysBack < 0) {
			daysBack += 7;
		}

		return dateMoment.clone().subtract(daysBack, 'days').format('YYYY-MM-DD');
	};

	// Use currentWeekStart directly if provided (it's already the business week start from SchedulePage)
	// Otherwise calculate it from today's date
	const initialSourceWeekStart = currentWeekStart 
		? moment(currentWeekStart).format('YYYY-MM-DD') 
		: getFirstDayOfWeek(moment().format('YYYY-MM-DD'));
	const initialTargetWeekStart = moment(initialSourceWeekStart).add(7, 'days').format('YYYY-MM-DD');

	const { values, errors, touched, submitForm, setFieldValue, handleBlur } = useFormik<ICopyScheduleForm>({
		validationSchema,
		initialValues: {
			copyRangeType: 'week',
			sourceDate: initialSourceWeekStart,
			targetDate: initialTargetWeekStart,
			copyExactSchedule: true,
			copyToHouseSchedule: false,
		},
		onSubmit: async (data: ICopyScheduleForm) => {
			setIsLoading(true);
			try {
				// Calculate source and target date ranges based on copy type
				let sourceStartDate = data.sourceDate;
				let sourceEndDate = data.sourceDate;
				let targetStartDate = data.targetDate;
				let targetEndDate = data.targetDate;

				if (data.copyRangeType === 'week') {
					// For week, calculate the business week boundaries
					sourceStartDate = getFirstDayOfWeek(data.sourceDate);
					sourceEndDate = moment(sourceStartDate).add(6, 'days').format('YYYY-MM-DD');

					targetStartDate = getFirstDayOfWeek(data.targetDate);
					targetEndDate = moment(targetStartDate).add(6, 'days').format('YYYY-MM-DD');
				} else {
					// For single day, both start and end dates are the same
					sourceEndDate = data.sourceDate;
					targetEndDate = data.targetDate;
				}

				// Format dates with calendar day boundaries (00:00:00 to 23:59:59)
				const formattedTargetStartDate = `${targetStartDate} 00:00:00`;
				const formattedTargetEndDate = `${targetEndDate} 23:59:59`;

				// Check if there are existing shifts in the target date range
				const existingShifts = await fetchAllShifts({
					workplaceId: workplaceId!,
					fromDate: formattedTargetStartDate,
					toDate: formattedTargetEndDate,
				});

				const shiftCount = existingShifts?.member.length || 0;
				console.log('Existing shifts in target range:', existingShifts, { 
					targetStartDate, 
					targetEndDate,
					formattedTargetStartDate,
					formattedTargetEndDate 
				});

				if (shiftCount > 0) {
					// Shifts exist, show confirmation dialog
					setExistingShiftCount(shiftCount);
					setPendingCopyData({ data, sourceStartDate, sourceEndDate, targetStartDate });
					setShowConfirmDialog(true);
					setIsLoading(false);
				} else {
					// No existing shifts, proceed with copy
					await performCopy(data, sourceStartDate, sourceEndDate, targetStartDate);
				}
			} catch (error: any) {
				showToast('error', error?.message || 'Failed to check existing shifts');
				setIsLoading(false);
			}
		},
	});

	const performCopy = async (data: ICopyScheduleForm, sourceStartDate: string, sourceEndDate: string, targetStartDate: string) => {
		setIsLoading(true);
		try {
			const payload = {
				workplace: `api/workplaces/${workplaceId}`,
				source_range: {
					start: `${sourceStartDate} 00:00:00`,
					end: `${sourceEndDate} 23:59:59`,
				},
				target_start: `${targetStartDate} 00:00:00`,
				options: [{
					copy_exact_schedule: data.copyExactSchedule,
					copy_to_house_schedule: data.copyToHouseSchedule,
				}],
			};

			await copyShifts(payload);
			queryClient.invalidateQueries(['shifts']);
			showToast('success', 'Schedule copied successfully!');
			onBack();
		} catch (error: any) {
			showToast('error', error?.message || 'Failed to copy schedule');
		} finally {
			setIsLoading(false);
		}
	};

	const handleConfirmCopy = async () => {
		setShowConfirmDialog(false);
		if (pendingCopyData) {
			const { data, sourceStartDate, sourceEndDate, targetStartDate } = pendingCopyData;
			await performCopy(data, sourceStartDate, sourceEndDate, targetStartDate);
			setPendingCopyData(null);
		}
	};

	const handleCancelCopy = () => {
		setShowConfirmDialog(false);
		setPendingCopyData(null);
		setIsLoading(false);
	};

	const handleSubmitForm = (e: any) => {
		e.preventDefault();
		submitForm();
	};

	// Calculate duration for display
	const getDurationText = () => {
		if (values.copyRangeType === 'day') {
			return `1 day on ${moment(values.sourceDate).format('MMM D, YYYY')} to ${moment(values.targetDate).format('MMM D, YYYY')}`;
		} else {
			const weekStart = moment(getFirstDayOfWeek(values.sourceDate));
			const weekEnd = weekStart.clone().add(6, 'days');
			
			const targetWeekStart = moment(getFirstDayOfWeek(values.targetDate));
			const targetWeekEnd = targetWeekStart.clone().add(6, 'days');
			
			return `1 week from ${weekStart.format('MMM D, YYYY')} - ${weekEnd.format('MMM D, YYYY')} to ${targetWeekStart.format('MMM D, YYYY')} - ${targetWeekEnd.format('MMM D, YYYY')}`;
		}
	};

	return (
		<Paper sx={{ minHeight: '100vh', py: 3, borderRadius: 3, boxShadow: 2 }}>
			<Box maxWidth="1100px" mx="auto" px={3}>
				<Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
					<Button
						onClick={onBack}
						sx={{
							backgroundColor: 'rgb(68, 99, 136)',
							color: '#fff',
							textTransform: 'none',
							fontWeight: 500,
							px: 3,
							py: 0.75,
							'&:hover': { backgroundColor: 'rgb(57, 80, 111)' },
						}}
					>
						Back
					</Button>

					<Box sx={{ fontSize: '1.5rem', fontWeight: 600 }}>Copy Schedule</Box>

					<Button
							type="submit"
							variant="contained"
							form="copyScheduleForm"
							sx={{
								backgroundColor: 'rgb(68, 99, 136)',
								color: '#fff',
								px: 4,
								textTransform: 'none',
								'&:hover': { backgroundColor: 'rgb(57, 80, 111)' },
							}}
							disabled={isLoading}
						>
					{isLoading ? 'Copying...' : 'Copy'}
				</Button>
			</Box>

			<Box sx={{ p: 4 }}>
				<form id="copyScheduleForm" onSubmit={handleSubmitForm}>
							<Grid container spacing={2}>
								{/* Copy Range Type Selection */}
								<Grid item xs={12}>
									<h3 style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: '16px', color: '#555' }}>
										Copy Range Type
									</h3>
									<RadioGroup
										row
										value={values.copyRangeType}
										onChange={(e) => setFieldValue('copyRangeType', e.target.value)}
									>
										<FormControlLabel
											value="day"
											control={<Radio sx={{ color: 'rgb(68, 99, 136)', '&.Mui-checked': { color: 'rgb(68, 99, 136)' } }} />}
											label="Single Day"
										/>
										<FormControlLabel
											value="week"
											control={<Radio sx={{ color: 'rgb(68, 99, 136)', '&.Mui-checked': { color: 'rgb(68, 99, 136)' } }} />}
											label="Single Week"
										/>
									</RadioGroup>
								</Grid>

								{/* Source Section */}
								<Grid item xs={12} sx={{ mt: 2 }}>
									<h3 style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: '16px', color: '#555' }}>
										Source Schedule
									</h3>
								</Grid>

								{/* Source Date */}
								<Grid item xs={12} sm={6}>
									<TextField
										name="sourceDate"
									label={values.copyRangeType === 'day' ? 'Copy From Date' : `Copy From Week (${startOfWeek || 'First day of week'})`}
										type="date"
										required
										fullWidth
										variant="outlined"
										margin="normal"
										value={values.sourceDate}
										onChange={(e) => {
											let newDate = e.target.value;
											if (values.copyRangeType === 'week') {
											// Snap to business week start day
												newDate = getFirstDayOfWeek(newDate);
											}
											setFieldValue('sourceDate', newDate);
										}}
										onBlur={handleBlur}
										error={touched.sourceDate && Boolean(errors.sourceDate)}
										helperText={touched.sourceDate && errors.sourceDate}
										InputLabelProps={{ shrink: true }}
									/>
								</Grid>

								{/* Target Section */}
								<Grid item xs={12} sx={{ mt: 2 }}>
									<h3 style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: '16px', color: '#555' }}>
										Target Schedule
									</h3>
								</Grid>

								{/* Target Date */}
								<Grid item xs={12} sm={6}>
									<TextField
										name="targetDate"
									label={values.copyRangeType === 'day' ? 'Copy To Date' : `Copy To Week (${startOfWeek || 'First day of week'})`}
										type="date"
										required
										fullWidth
										variant="outlined"
										margin="normal"
										value={values.targetDate}
										onChange={(e) => {
											let newDate = e.target.value;
											if (values.copyRangeType === 'week') {
											// Snap to business week start day
												newDate = getFirstDayOfWeek(newDate);
											}
											setFieldValue('targetDate', newDate);
										}}
										onBlur={handleBlur}
										error={touched.targetDate && Boolean(errors.targetDate)}
										helperText={touched.targetDate && errors.targetDate}
										InputLabelProps={{ shrink: true }}
									/>
								</Grid>

								{/* Options Section */}
								<Grid item xs={12} sx={{ mt: 2 }}>
									<h3 style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: '16px', color: '#555' }}>
										Copy Options
									</h3>
								</Grid>

								{/* Copy Exact Schedule */}
								<Grid item xs={12}>
									<FormControlLabel
										control={
											<Checkbox
												checked={values.copyExactSchedule}
												onChange={(e) => {
													setFieldValue('copyExactSchedule', e.target.checked);
													if (e.target.checked) {
														setFieldValue('copyToHouseSchedule', false);
													}
												}}
												name="copyExactSchedule"
												sx={{
													color: 'rgb(68, 99, 136)',
													'&.Mui-checked': {
														color: 'rgb(68, 99, 136)',
													},
												}}
											/>
										}
										label="Copy Schedule As-Is (Preserve employee assignments)"
										sx={{ mb: 1 }}
									/>
									<p style={{ margin: '0 0 0 32px', fontSize: '13px', color: '#666' }}>
										The schedule will be copied with the same employee assignments
									</p>
								</Grid>

								{/* Copy To House Schedule */}
								<Grid item xs={12}>
									<FormControlLabel
										control={
											<Checkbox
												checked={values.copyToHouseSchedule}
												onChange={(e) => {
													setFieldValue('copyToHouseSchedule', e.target.checked);
													if (e.target.checked) {
														setFieldValue('copyExactSchedule', false);
													}
												}}
												name="copyToHouseSchedule"
												sx={{
													color: 'rgb(68, 99, 136)',
													'&.Mui-checked': {
														color: 'rgb(68, 99, 136)',
													},
												}}
											/>
										}
										label="Copy All Shifts as House Shifts (Unassigned)"
										sx={{ mb: 1 }}
									/>
									<p style={{ margin: '0 0 0 32px', fontSize: '13px', color: '#666' }}>
										When checked all shifts will be copied and made into House Shifts (unassigned)
									</p>
								</Grid>

								{/* Information */}
								<Grid item xs={12}>
									<Box
										sx={{
											backgroundColor: '#e3f2fd',
											border: '1px solid #90caf9',
											borderRadius: '4px',
											padding: '12px 16px',
											mt: 2,
										}}
									>
										<p style={{ margin: 0, fontSize: '14px', color: '#1976d2' }}>
											<strong>Will copy:</strong> {getDurationText()}
										</p>
									</Box>
								</Grid>
							</Grid>
						</form>
					</Box>

					{/* Confirmation Dialog */}
					<Dialog
					open={showConfirmDialog}
					onClose={handleCancelCopy}
					maxWidth="sm"
					fullWidth
				>
					<DialogTitle sx={{ fontWeight: 600 }}>
						Existing Shifts Found
					</DialogTitle>
					<DialogContent>
						<DialogContentText sx={{ color: '#333' }}>
							There {existingShiftCount === 1 ? 'is' : 'are'} already {existingShiftCount} shift{existingShiftCount !== 1 ? 's' : ''} in the target date range.
							Copying will add new shifts without removing existing ones.
							<br /><br />
							Do you want to proceed with copying the schedule?
						</DialogContentText>
					</DialogContent>
					<DialogActions sx={{ p: 2 }}>
						<Button
							onClick={handleCancelCopy}
							variant="outlined"
							sx={{
								textTransform: 'none',
								color: 'rgb(68, 99, 136)',
								borderColor: 'rgb(68, 99, 136)',
								'&:hover': {
									borderColor: 'rgb(57, 80, 111)',
									backgroundColor: 'rgba(68, 99, 136, 0.04)',
								},
							}}
						>
							Cancel
						</Button>
						<Button
							onClick={handleConfirmCopy}
							variant="contained"
							disabled={isLoading}
							sx={{
								textTransform: 'none',
								backgroundColor: 'rgb(68, 99, 136)',
								'&:hover': { backgroundColor: 'rgb(57, 80, 111)' },
							}}
						>
							{isLoading ? 'Copying...' : 'Continue'}
						</Button>
					</DialogActions>
				</Dialog>
			</Box>
		</Paper>
	);
};

export default CopySchedulePage;
